import React, { useCallback, useMemo } from "react";

import { faFileEdit } from "@fortawesome/pro-solid-svg-icons";
import { Grid } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components/macro";

import { openDispatchingModal } from "actions";

import { ColDef } from "ag-grid-community";

import AgGridTable from "components/AgGrid/AgGridContainer";
import TableWrapper from "components/AgGrid/TableWrapper";
import { Column, Row } from "components/Layout";
import WaitForSync from "components/LoadingSpinner/WaitForSync";
import { ruleColumnDefs } from "components/RuleBookTable/columns";

import { AgGridTables } from "constants/aggrid";
import { ApiModel } from "constants/loading";
import { ModalTypes } from "constants/navigation";
import { RuleSetPermissions } from "constants/permissions";
import { colors } from "constants/theme";

import { hasPermission } from "lib/permissions";

import { getIsStaff, getRuleSetById } from "selectors";

import { selectRulesAggridDataByRuleSetIdLookup } from "selectors/aggrid/rules";

import { RuleSetSummary } from "./RuleSetSummary";
import { RuleSetSummaryForm } from "./RuleSetSummaryForm";

export const InfoSpan = styled.span`
  color: ${colors.gray40};
`;

const RuleSetSummaryGrid = styled(Grid)`
  background: ${({ theme }) => theme.colors.grayF3};
  border-radius: 4px;
  padding: ${({ theme }) => theme.space[2]}px;
  margin-bottom: ${({ theme }) => theme.space[2]}px;
`;

interface RuleSetSummaryComponentProps {
  openEditSettings: () => void;
  isEditSettingsOpen: boolean;
  readOnly: boolean;
  isManagedRule: boolean;
}

interface RuleSetTableProps {
  ruleSetId: string;
  isEditSettingsOpen: boolean;
  openEditSettings: () => void;
  isManagedRule: boolean;
}

const getRowId = params => params.data.rule.id;

const RuleSetSummaryComponent = ({
  openEditSettings,
  isEditSettingsOpen,
  readOnly,
  isManagedRule,
}: RuleSetSummaryComponentProps) => (
  <RuleSetSummaryGrid container xs={12}>
    {isEditSettingsOpen ? (
      <RuleSetSummaryForm isManagedRule={isManagedRule} />
    ) : (
      <RuleSetSummary readOnly={readOnly} openEditSettings={openEditSettings} />
    )}
  </RuleSetSummaryGrid>
);

export const RuleSetTable = ({
  ruleSetId,
  isEditSettingsOpen,
  openEditSettings,
  isManagedRule,
}: RuleSetTableProps) => {
  const rowData = useSelector(selectRulesAggridDataByRuleSetIdLookup)[
    ruleSetId
  ];

  const ruleSet = useSelector(getRuleSetById(ruleSetId));

  const readOnly = !hasPermission(ruleSet, RuleSetPermissions.update);

  const masterRuleSetId: string | null = ruleSet?.master_rule_set_id || null;

  const isStaff = useSelector(getIsStaff);

  const dispatch = useDispatch();

  const openEditMasterRuleSet = useCallback(() => {
    dispatch(
      openDispatchingModal(
        ModalTypes.EditMasterRuleSet,
        window.location.hash,
        { masterRuleSetId },
        { masterRuleSetId },
      ),
    );
  }, [dispatch, masterRuleSetId]);

  const additionalActions = useMemo(
    () =>
      isStaff && masterRuleSetId
        ? [
            {
              title: "Edit Master Rule Set (Staff Only)",
              icon: faFileEdit,
              onClick: openEditMasterRuleSet,
              dataTour: "testRules",
            },
          ]
        : null,
    [isStaff, masterRuleSetId, openEditMasterRuleSet],
  );

  const columnDefs = useMemo(
    () =>
      ruleColumnDefs({
        isInEditRuleSetModal: true,
      }) as ColDef[],
    [],
  );

  return (
    <Column fullHeight>
      <Row>
        <RuleSetSummaryComponent
          openEditSettings={openEditSettings}
          isEditSettingsOpen={isEditSettingsOpen}
          readOnly={readOnly}
          isManagedRule={isManagedRule}
        />
      </Row>
      <WaitForSync
        requiredData={[ApiModel.RULE_BOOKS, ApiModel.RULE_SETS, ApiModel.RULES]}
      >
        <AgGridTable
          additionalActions={additionalActions}
          columnDefs={columnDefs}
          getRowId={getRowId}
          rowData={rowData}
          showGlobalSearchFilter={false}
          WrapperComponent={TableWrapper}
          tableName={AgGridTables.RULE_BOOK}
          enableBrowserTooltips
        />
      </WaitForSync>
    </Column>
  );
};
